import { useLocation, useNavigate, useSearch } from '@tanstack/react-router';
import {
  Button,
  Icon,
  IconProps,
  Modal,
  ModalFooter,
  ModalHeader,
  PopoverMenu,
  SideNav,
  SideNavOptionProps,
  ToolTip,
} from '@yembo/yemblocks-core';
import { Ref, useContext, useState } from 'react';
import { putLogout } from 'src/queries/Login';
import { useWindowDimensions } from '../_hooks/useWindowDimensions';
import { useDeniedRootRoutes } from '../_routes/_hooks/useDeniedRootRoutes';
import { AddContactContext } from '../_shared/Context/AddContact/context';
import { AuthUserContext } from '../_shared/Context/AuthUserContext';
import { OnlineStatusContext } from '../_sw/OnlineStatusProvider/context';
import { SIDEBAR_OPEN_THRESHOLD } from '../_util/ScreenConstants';
import { Contact } from '../Contact';
import { AvatarSection } from './AvatarSection';

type SidebarIconProps = {
  iconName: IconProps['name'];
  isCollapsed: boolean;
  tooltipText: string;
};

const SidebarIcon = ({ iconName, isCollapsed, tooltipText }: SidebarIconProps) =>
  !isCollapsed ? (
    <Icon name={iconName} />
  ) : (
    <ToolTip position='right' content={tooltipText}>
      <Icon name={iconName} />
    </ToolTip>
  );

type SideBarProps = {
  ref: Ref<HTMLDivElement>;
};

export const Sidebar = ({ ref }: SideBarProps) => {
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [_isIntercomModalOpen, _setIsIntercomModalOpen] = useState(false);

  const { authUser } = useContext(AuthUserContext);

  const { isOnline } = useContext(OnlineStatusContext);

  const searchParams = useSearch({ strict: false }) as Record<string, string | undefined>;
  const navigate = useNavigate();
  const setSearchParams = (
    val:
      | Record<string, string | undefined>
      | ((prev: Record<string, string | undefined>) => Record<string, string | undefined>)
  ) =>
    navigate({
      // biome-ignore lint/suspicious/noExplicitAny: TanStack Router search params typing is complex in this context
      search: (typeof val === 'function' ? val(searchParams) : { ...searchParams, ...val }) as any,
    });
  const { pathname } = useLocation();
  const basePath = pathname.split('/')[1];

  const isRoomReview = pathname.split('/')[3] === 'rooms' && !!searchParams?.key;
  const isJobDetail = pathname.startsWith('/job/');

  const { isAddContactOpen } = useContext(AddContactContext);

  const { width } = useWindowDimensions();
  const deniedRoutes = useDeniedRootRoutes();

  const checkIsActive = (basePath: string) => !isAddContactOpen && pathname.startsWith(basePath);

  const isCollapsed = width <= SIDEBAR_OPEN_THRESHOLD || isRoomReview || isJobDetail;

  const navItems: SideNavOptionProps[] = [
    {
      key: 'job',
      icon: <SidebarIcon isCollapsed={isCollapsed} iconName='list' tooltipText='Jobs' />,
      text: 'Jobs',
      isActive: checkIsActive('/job'),
      onClick: () => navigate({ to: '/jobs' }),
      href: '/jobs',
    },
    {
      key: 'locations',
      icon: <SidebarIcon isCollapsed={isCollapsed} iconName='map-pin' tooltipText='Locations' />,
      text: 'Locations',
      isActive: checkIsActive('/locations'),
      onClick: () => navigate({ to: '/locations' }),
      href: '/locations',
    },
    {
      key: 'admin',
      icon: <SidebarIcon isCollapsed={isCollapsed} iconName='shield' tooltipText='Admin' />,
      text: 'Admin',
      isActive: checkIsActive('/admin'),
      // Employees is the default tab; /admin redirects there.
      onClick: () => navigate({ to: '/admin/employees' }),
      href: '/admin/employees',
    },
  ].filter(({ key }) => !deniedRoutes.has(key));

  const getShortName = () => {
    return [authUser?.givenName, authUser?.familyName?.[0]].filter(Boolean).join(' ') || 'User';
  };

  const getFullName = () => {
    return [authUser?.givenName, authUser?.familyName].filter(Boolean).join(' ') || 'User';
  };

  const avatarSectionProps = {
    userName: getShortName(),
    photoUrl: authUser?.photoUrl ?? '',
    role: authUser?.role,
  };

  const popoverMenuOptions = [
    {
      name: <AvatarSection {...avatarSectionProps} userName={getFullName()} />,
      className: 'avatar-option',
    },
    {
      name: 'Settings',
      onClick: () => basePath !== 'settings' && navigate({ to: '/settings' }),
    },
    {
      name: 'Log Out',
      onClick: () => setIsLogoutModalOpen(true),
    },
  ];

  const isMobileLandscapeView = window.matchMedia('(max-height: 450px)').matches;

  return (
    <>
      <SideNav
        ref={ref}
        className={`sidebar-section ${!isOnline ? 'offline' : ''}`}
        navItems={navItems}
        isCollapsed={isCollapsed}
        bottomContent={
          <PopoverMenu
            isDisabled={!isOnline}
            className='sidebar-menu-wrapper'
            menuClassName='sidebar-menu'
            clickableElement={
              <AvatarSection
                {...avatarSectionProps}
                avatarSize={isMobileLandscapeView ? 'small' : 'medium'}
                isCollapsed={isCollapsed}
              />
            }
            menuOptions={popoverMenuOptions}
          />
        }
      />
      {isAddContactOpen && <Contact />}
      {isLogoutModalOpen && (
        <Modal isDismissible={false}>
          <ModalHeader headerText='Logout'>Are you sure you want to log out from this device?</ModalHeader>
          <ModalFooter>
            <Button kind='outline' text='Cancel' onClick={() => setIsLogoutModalOpen(false)} />
            <Button
              text='Logout'
              onClick={() => {
                setSearchParams({});
                signOutFlow();
              }}
              isDanger
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

const signOutFlow = async () => {
  try {
    await putLogout();
  } catch {
    // Signing out of this browser does not depend on the server's answer.
  }
  localStorage.clear();
  window.location.assign('/login?nextPage=/jobs');
};
